import { RELEASE_SQUARESPACE_WEBSITES_RESELLER } from '#client/constants/experimentsFlags';

import WebsiteEligibilityGate from '../components/gates/WebsiteEligibilityGate';
import { globalEntitlements } from '../utils/globalEntitlements';
import { buildStaticWebsiteSqspCtaUrl, STATIC_WEBSITE_SQSP_NOTE_VALUES } from '../utils/staticWebsiteSqsp';

/** @typedef {import('../types').ProductLandingConfig} ProductLandingConfig */

const BUY_BOX_BULLET_POINTS = [
  { title: 'productPage.staticWebsiteSqsp.buyBox.bulletPoint1' },
  { title: 'productPage.staticWebsiteSqsp.buyBox.bulletPoint2' },
  { title: 'productPage.staticWebsiteSqsp.buyBox.bulletPoint3' },
];

const BUY_BOX_NOTE = {
  note: 'productPage.staticWebsiteSqsp.buyBox.note',
  noteValues: STATIC_WEBSITE_SQSP_NOTE_VALUES,
};

const WEBSITE_SQSP_INTERACT_SERVICE = {
  service: 'website_sqsp_core',
  buildCtaUrl: buildStaticWebsiteSqspCtaUrl,
};

/** @type {ProductLandingConfig} */
export const STATIC_WEBSITE_SQSP_CONFIG = {
  productGates: {
    flagGate: {
      flagKey: RELEASE_SQUARESPACE_WEBSITES_RESELLER,
      redirectPath: '/products',
    },
    componentGates: [
      {
        Component: WebsiteEligibilityGate,
      },
    ],
  },
  hero: {
    defaultPackageIndex: 0,
    buyBox: [
      {
        bulletPoints: BUY_BOX_BULLET_POINTS,
        packageName: 'productPage.staticWebsiteSqsp.optionLabel.annual',
        packageNote: 'productPage.staticWebsiteSqsp.optionLabel.annualNote',
        productName: 'productPage.staticWebsiteSqsp.buyBox.productName',
        showAnnualPriceAsMonthly: true,
        shortMonthlyPrice: true,
        priceNote: 'productPage.staticWebsiteSqsp.buyBox.billedUpfront',
        veloCta: {
          displayVeloCta: true,
          promptMessageId: 'productPage.staticWebsiteSqsp.buyBox.veloCta.prompt',
          topic: 'squarespace-website',
        },
        interactService: {
          ...WEBSITE_SQSP_INTERACT_SERVICE,
          interval: 'annual',
        },
        ...BUY_BOX_NOTE,
      },
      {
        bulletPoints: BUY_BOX_BULLET_POINTS,
        packageName: 'productPage.staticWebsiteSqsp.optionLabel.monthly',
        packageNote: 'productPage.staticWebsiteSqsp.optionLabel.monthlyNote',
        productName: 'productPage.staticWebsiteSqsp.buyBox.productName',
        shortMonthlyPrice: true,
        veloCta: {
          displayVeloCta: true,
          promptMessageId: 'productPage.staticWebsiteSqsp.buyBox.veloCta.prompt',
          topic: 'squarespace-website',
        },
        interactService: {
          ...WEBSITE_SQSP_INTERACT_SERVICE,
          interval: 'monthly',
        },
        ...BUY_BOX_NOTE,
      },
    ],
    headerPicture: {
      src: '/s2/raster/woman-laptop-pen.png',
      alt: 'productPage.staticWebsiteSqsp.heroPicture.altText',
    },
    headline: 'productPage.staticWebsiteSqsp.page.headline',
    partnerLogo: {
      src: '/s2/vector/squarespace-logo.svg',
      alt: 'productPage.staticWebsiteSqsp.heroPartnerLogo.altText',
    },
    partnerReviewBlanks: [],
    subheader: 'productPage.staticWebsiteSqsp.page.subheader',
  },
  benefit: {
    title: 'productPage.staticWebsiteSqsp.valueProp.title',
    items: [
      {
        icon: '/s2/material-icons/DesignServicesRounded.svg',
        title: 'productPage.staticWebsiteSqsp.valueProp.propTitle1',
        description: 'productPage.staticWebsiteSqsp.valueProp.shortCopy1',
      },
      {
        icon: '/s2/material-icons/DashboardCustomizeRounded.svg',
        title: 'productPage.staticWebsiteSqsp.valueProp.propTitle2',
        description: 'productPage.staticWebsiteSqsp.valueProp.shortCopy2',
      },
      {
        icon: '/s2/material-icons/AccountTreeRounded.svg',
        title: 'productPage.staticWebsiteSqsp.valueProp.propTitle3',
        description: 'productPage.staticWebsiteSqsp.valueProp.shortCopy3',
      },
    ],
  },
  howItWorks: {
    title: 'productPage.staticWebsiteSqsp.howItWorks.title',
    items: [
      {
        title: 'productPage.staticWebsiteSqsp.howItWorks.step1',
        description: 'productPage.staticWebsiteSqsp.howItWorks.shortDescription1',
      },
      {
        title: 'productPage.staticWebsiteSqsp.howItWorks.step2',
        description: 'productPage.staticWebsiteSqsp.howItWorks.shortDescription2',
      },
      {
        title: 'productPage.staticWebsiteSqsp.howItWorks.step3',
        description: 'productPage.staticWebsiteSqsp.howItWorks.shortDescription3',
      },
    ],
    image: '/s2/raster/squarespace-website-example.png',
    alt: 'productPage.staticWebsiteSqsp.howItWorks.altText',
    hasNumber: true,
    hasDivider: false,
    hasMobileImage: true,
  },
  highlight: {
    icon: '/s2/material-icons/WebRounded.svg',
    title: 'productPage.staticWebsiteSqsp.topFeature.title',
    subheader: 'productPage.staticWebsiteSqsp.topFeature.subheader',
    image: '/s2/raster/man-woman-laptop-at-home.png',
    alt: 'productPage.staticWebsiteSqsp.topFeature.altText',
  },
  faqs: {
    title: 'productPage.staticWebsiteSqsp.frequentlyAskedQuestions.title',
    items: [
      {
        question: 'productPage.staticWebsiteSqsp.frequentlyAskedQuestions.faq1',
        answer: 'productPage.staticWebsiteSqsp.frequentlyAskedQuestions.faqAnswer1',
      },
      {
        question: 'productPage.staticWebsiteSqsp.frequentlyAskedQuestions.faq2',
        answer: 'productPage.staticWebsiteSqsp.frequentlyAskedQuestions.faqAnswer2',
      },
      {
        question: 'productPage.staticWebsiteSqsp.frequentlyAskedQuestions.faq3',
        answer: 'productPage.staticWebsiteSqsp.frequentlyAskedQuestions.faqAnswer3',
      },
      {
        question: 'productPage.staticWebsiteSqsp.frequentlyAskedQuestions.faq4',
        answer: 'productPage.staticWebsiteSqsp.frequentlyAskedQuestions.faqAnswer4',
      },
    ],
  },
  assistance: {
    title: 'productPage.staticWebsiteSqsp.needAssistance.title',
    getInTouch: 'productPage.staticWebsiteSqsp.needAssistance.getInTouch',
    actions: 'productPage.staticWebsiteSqsp.needAssistance.actions',
  },
  getBuyButtonConfig: globalEntitlements({
    ctaText: 'productPage.staticWebsiteSqsp.buyBox.buttonLabel',
  }).build(),
};
